import { useCallback, useMemo, useRef } from "react";

import { sortUser } from "@/domain/user/models/user-model";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { UserWithTeamMembershipList } from "../../models/team-membership-model";
import { TeamMembershipStatus } from "../../models/team-model";
import { TeamMembershipRole } from "../../models/team-model";

import { teamListForWorkspaceQueryDocument } from "./team.gql";

export interface UseGetTeamListProps {
  workspaceId?: string;
  shouldUpdateCache?: boolean;
  enabled?: boolean;
}

export const useTeamListForWorkspace = ({ workspaceId, enabled = true }: UseGetTeamListProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.teams.list(workspaceId ?? ""),
    queryFn: async () => {
      const client = createOperationClient("teamListForWorkspace");
      return await client.request(teamListForWorkspaceQueryDocument, { input: workspaceId ?? "" });
    },
    enabled: !!workspaceId && enabled,
  });

  const teamList = useMemo(() => {
    if (!queryResult.data) {
      return [];
    }
    return queryResult.data.teamListForWorkspace;
  }, [queryResult.data]);

  const getTeamListForUser = useCallback(
    (
      userId: string,
      filters?: {
        statusList?: string[]; // Optional list of statuses to filter by
        roleList?: string[]; // Optional list of roles to filter by
      },
    ) => {
      if (!queryResult.data) {
        return [];
      }

      return queryResult.data.teamListForWorkspace.filter((team) => {
        return team.teamMembershipList.some((membership) => {
          const statusMatches = !filters?.statusList || filters.statusList.includes(membership.status);
          const roleMatches = !filters?.roleList || filters.roleList.includes(membership.role);
          const userMatches = membership.userId === userId;

          return statusMatches && roleMatches && userMatches;
        });
      });
    },
    [queryResult.data],
  );

  const getMembershipListForTeams = useCallback(
    (
      teamIdList: string[],
      filters?: {
        roleList?: TeamMembershipRole[];
        statusList?: TeamMembershipStatus[];
      },
    ) => {
      return teamList
        .filter((team) => teamIdList.includes(team.id)) // Filter by team IDs
        .flatMap((team) =>
          team.teamMembershipList.filter((membership) => {
            const roleMatches = !filters?.roleList || filters.roleList.includes(membership.role as TeamMembershipRole);
            const statusMatches =
              !filters?.statusList || filters.statusList.includes(membership.status as TeamMembershipStatus);
            return roleMatches && statusMatches;
          }),
        );
    },
    [teamList],
  );

  const cacheRef = useRef(new Map<string, UserWithTeamMembershipList[]>());
  const getUserWithMembershipListForTeams = useCallback(
    (
      teamIdList: string[],
      filters?: {
        roleList?: TeamMembershipRole[];
        statusList?: TeamMembershipStatus[];
      },
    ) => {
      // Create a cache key based on the arguments and the reference of `getMembershipListForTeams`
      // We do this so the datatable can rerender more efficiently
      const cacheKey = JSON.stringify({
        teamIdList,
        filters,
        getMembershipListForTeams: getMembershipListForTeams.toString(), // Include function reference
      });

      // Check if the result is already cached
      if (cacheRef.current.has(cacheKey)) {
        return cacheRef.current.get(cacheKey) ?? [];
      }

      // Compute the result
      const filteredMemberships = getMembershipListForTeams(teamIdList, filters);

      const userMap = new Map<string, UserWithTeamMembershipList>();

      filteredMemberships.forEach((membership) => {
        if (!membership.user) {
          return;
        }
        const existingUser = userMap.get(membership.user.id);

        if (existingUser) {
          existingUser.teamMembershipList.push(membership);
        } else {
          userMap.set(membership.user.id, {
            ...membership.user,
            teamMembershipList: [membership],
          } as UserWithTeamMembershipList);
        }
      });

      const result = Array.from(userMap.values()).sort((a, b) => sortUser(a, b, false));

      // Cache the result
      cacheRef.current.set(cacheKey, result);

      return result;
    },
    [getMembershipListForTeams],
  );

  // Note: Cache updates are now handled automatically by React Query
  // The shouldUpdateCache prop is no longer needed with React Query

  return {
    teamList,
    loading: queryResult.isLoading,
    error: queryResult.error,
    queryResult,
    getTeamListForUser,
    getMembershipListForTeams,
    getUserWithMembershipListForTeams,
  };
};
